import { getAuth } from "firebase/auth";
import { getExpressApiBaseUrl } from "./config";

/**
 * Upload a single image and return the public URL
 * @param {string} base64Image - Base64 encoded image data
 * @param {string} originalName - Optional original filename
 * @returns {Promise<{ url: string, imageId: string, size: number, mimeType: string }>}
 */
export async function uploadImage(base64Image, originalName) {
  const user = getAuth().currentUser;
  if (!user?.uid) {
    throw new Error("Please log in to continue.");
  }

  const token = await user.getIdToken();
  if (!token) {
    throw new Error("Authentication failed. Please try logging in again.");
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);

  try {
    const response = await fetch(`${getExpressApiBaseUrl()}/upload-image`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
      body: JSON.stringify({
        image: base64Image,
        originalName
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text().catch(() => "");
      throw new Error(`Upload failed: ${response.status} ${errorText}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Upload failed");
    }

    return {
      url: data.url,
      imageId: data.imageId,
      size: data.size,
      mimeType: data.mimeType
    };
  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === "AbortError") {
      throw new Error("Upload timed out. Please try again.");
    }
    throw err;
  }
}

/**
 * Upload multiple images in batch
 * @param {Array<{ image: string, originalName?: string }>} images 
 * @returns {Promise<{ results: Array, errors?: Array, totalUploaded: number, totalFailed: number }>}
 */
export async function uploadImages(images) {
  const user = getAuth().currentUser;
  if (!user?.uid) {
    throw new Error("Please log in to continue.");
  }

  const token = await user.getIdToken();
  if (!token) {
    throw new Error("Authentication failed. Please try logging in again.");
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 60000); // Longer timeout for batch

  try {
    const response = await fetch(`${getExpressApiBaseUrl()}/upload-images`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
      body: JSON.stringify({ images }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text().catch(() => "");
      throw new Error(`Batch upload failed: ${response.status} ${errorText}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Batch upload failed");
    }

    return {
      results: data.results || [],
      errors: data.errors,
      totalUploaded: data.totalUploaded || 0,
      totalFailed: data.totalFailed || 0
    };
  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === "AbortError") {
      throw new Error("Upload timed out. Please try again.");
    }
    throw err;
  }
}

/**
 * Helper function to determine if a string is a base64 image
 */
export function isBase64Image(str) {
  if (typeof str !== 'string') return false;
  return /^data:image\/[^;]+;base64,/.test(str);
}

/**
 * Helper function to determine if a string is an image URL
 */
export function isImageUrl(str) {
  if (typeof str !== 'string') return false;
  try {
    const url = new URL(str);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}